/**
 * Desktop screens (from 1024 px): Bugün (artboard F_Masaustu), Onaylar with a detail pane,
 * İstekler as a card grid and Ekip (artboard F_EkipMasaustu). On desktop a T3 item can be
 * approved where its full text or diff is on screen; T4 still needs the 1.5 s hold.
 */

import { type RefObject, useEffect, useRef, useState } from 'react';
import type { Proposal, Snapshot, TwinRequest } from '../api/types.js';
import { usePanel } from '../app/context.js';
import { Link, navigate } from '../app/router.js';
import {
  AGENT_NAMES,
  AGENT_SHAPES,
  agentCounts,
  canEdit,
  counts,
  inbox,
  inboxParts,
  oldestApprovalAge,
  openRequests,
  orderedProposals,
  proposalGroups,
  requiresHold,
  teamSubline,
} from '../app/selectors.js';
import {
  CheckStrip,
  DesktopDecision,
  DiffBlock,
  DraftBlock,
  GenericDiff,
  GenericFields,
  GenericPreview,
  QuotedInput,
  VerifierNotice,
} from '../components/evidence.js';
import type { HoldHandle } from '../components/HoldButton.js';
import { AgentGlyph, Icon } from '../components/icons.js';
import { OrganizationToggle } from '../components/organization.js';
import { Kbd, LiveDot, SectionHeader, StatusPill, TypeChip } from '../components/primitives.js';
import { ExpiredLine, PulseTiles } from '../components/pulse.js';
import { ApprovalRow, RequestRow, T4Band } from '../components/queue.js';
import { ReadinessPanel } from '../components/readiness.js';
import { useSheets } from '../components/sheets.js';
import { TopBar, TopBarStatus } from '../components/shell.js';
import { AgentTile, HandoffLanes, LiveFeed, ProgramRuler, RunLog } from '../components/team.js';
import { TodaySection } from '../components/today.js';
import { SalesRow } from '../components/widgets.js';
import {
  budgetPace,
  formatAge,
  formatClock,
  formatClockSeconds,
  formatDuration,
  formatPct,
  formatQuotaPct,
  formatRelativeClock,
  formatUsd,
  formatUsdNumber,
  monthProgress,
} from '../lib/format.js';
import { QueueGroupHead } from './Bugun.js';
import { Accent, EmptyState, InboxParts, inboxTitle, Loading, ReviewButton, Segmented } from './common.js';
import { type ApproveVia, useDecision } from './decision.js';
import { PauseAllButton } from './Ekip.js';
import { RequestCard, requestSubline, useFocusRequest, useRequestTabs } from './Istekler.js';
import { ProposalBody } from './ProposalBody.js';

/**
 * J/K move, O approves (hold O for T4), D edits, R rejects. Ignored while typing or in a sheet.
 * A hold on O ends when the key comes up, when the window loses focus (the key-up would never
 * arrive) and when the selected item changes, so it can never complete on another item.
 */
function useDecisionKeys({
  enabled,
  proposal,
  onApprove,
  onEdit,
  onReject,
  onMove,
  holdRef,
}: {
  enabled: boolean;
  proposal: Proposal | null;
  onApprove: (via: ApproveVia) => void;
  onEdit: () => void;
  onReject: () => void;
  onMove?: (delta: 1 | -1) => void;
  holdRef: RefObject<HoldHandle | null>;
}) {
  const handlers = useRef({ onApprove, onEdit, onReject, onMove });
  handlers.current = { onApprove, onEdit, onReject, onMove };
  const sheets = useSheets();
  const sheetOpen = sheets.current !== null;
  useEffect(() => {
    if (!enabled || !proposal) return;
    const typing = (target: EventTarget | null) =>
      target instanceof HTMLElement && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));
    const onDown = (event: KeyboardEvent) => {
      if (sheetOpen || event.metaKey || event.ctrlKey || event.altKey || typing(event.target)) return;
      const key = event.key.toLocaleLowerCase('tr-TR');
      if (key === 'j' || key === 'k') {
        holdRef.current?.cancel();
        handlers.current.onMove?.(key === 'j' ? 1 : -1);
      } else if (key === 'o') {
        event.preventDefault();
        if (event.repeat) return;
        if (requiresHold(proposal)) holdRef.current?.start();
        else handlers.current.onApprove('tap');
      } else if (key === 'd' && canEdit(proposal)) handlers.current.onEdit();
      else if (key === 'r') handlers.current.onReject();
    };
    const onUp = (event: KeyboardEvent) => {
      if (event.key.toLocaleLowerCase('tr-TR') === 'o') holdRef.current?.cancel();
    };
    const onBlur = () => holdRef.current?.cancel();
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onBlur);
      holdRef.current?.cancel();
    };
  }, [enabled, proposal, sheetOpen, holdRef]);
}

/**
 * Desktop Bugün head (panel audit 25 Oct 2026, F2): the inbox number with its breakdown and "Sırayla incele"
 * beside the four business tiles in a 2x2 grid. Genesis spend moved off Bugün; Ekip shows it per agent.
 */
function InboxCard({ snapshot, now }: { snapshot: Snapshot; now: number }) {
  const box = inbox(snapshot);
  const c = counts(snapshot);
  const age = oldestApprovalAge(snapshot, now);
  return (
    <section className="inbox-card drafting" aria-labelledby="h-inbox">
      <h2 id="h-inbox" className="inbox-card__key key">
        <span className="kpi__marker" aria-hidden="true" />
        Senden beklenen
      </h2>
      <p className="inbox-card__title" aria-live="polite">
        {box.total === 0 ? 'Seni bekleyen bir şey yok' : inboxTitle(box)}
      </p>
      {inboxParts(box).length > 1 ? (
        <p className="inbox-card__parts">
          <InboxParts box={box} />
        </p>
      ) : null}
      <p className="inbox-card__line">
        {age !== null ? (
          <>
            en eski onay <span className="mono">{formatAge(age)}</span>
          </>
        ) : (
          'onay kuyruğu boş'
        )}
        {c.t4 > 0 ? ` · ${c.t4}'i para (T4)` : null}
      </p>
      <ReviewButton snapshot={snapshot} box={box} />
    </section>
  );
}

/** The first queue item opened inline, with its full text and the decision buttons. */
function InlineDecision({ proposal, snapshot, now }: { proposal: Proposal; snapshot: Snapshot; now: number }) {
  const holdRef = useRef<HoldHandle>(null);
  const decision = useDecision(proposal, () => {});
  const titleId = `inline-${proposal.id}`;
  // Bugün never holds: an item that needs the hold is only a link to its detail pane here.
  const hold = requiresHold(proposal);
  useDecisionKeys({
    enabled: !hold,
    proposal,
    onApprove: (via) => void decision.approve(via),
    onEdit: decision.edit,
    onReject: decision.reject,
    holdRef,
  });
  if (hold) {
    return (
      <>
        <T4Band desktop />
        <article className="inline-card" aria-labelledby={titleId}>
          <span className="inline-card__bar" aria-hidden="true" />
          <h3 id={titleId} className="inline-card__title">
            {proposal.title}
          </h3>
          <p className="inline-card__summary">{proposal.summary}</p>
          <Link to={`/onay/${proposal.id}`} className="btn btn--secondary">
            <Icon name="lock" size={16} />
            Ayrıntıda basılı tut
          </Link>
        </article>
      </>
    );
  }
  const run = snapshot.runs.find((item) => item.id === proposal.runId);
  const sourcesValue = proposal.sources.map((source) => (source.kind === 'kb' ? source.key : 'ürün kartı')).join(' · ');
  const checks = proposal.checks.map((check) =>
    check.key === 'Dayanak' && proposal.sources.length > 0 ? { ...check, value: sourcesValue } : check,
  );
  return (
    <article className="inline-card" aria-labelledby={titleId}>
      <span className="inline-card__bar" aria-hidden="true" />
      <div className="inline-card__tier-row">
        <span className="tier tier--t3">
          {proposal.tier} · {proposal.topic}
        </span>
        <h3 id={titleId} className="inline-card__kind">
          {proposal.kindLabel}
        </h3>
        <span className="inline-card__by">
          <AgentGlyph shape={AGENT_SHAPES[proposal.agent]} />
          {AGENT_NAMES[proposal.agent]} · <span className="mono">{formatClock(proposal.draftedAt)}</span> · bekliyor{' '}
          <span className="mono">{formatAge(now - proposal.queuedAt)}</span>
        </span>
      </div>
      {proposal.kind === 'customer_reply' ? <QuotedInput proposal={proposal} compact /> : null}
      <CheckStrip checks={checks} />
      {proposal.kind === 'customer_reply' ? <DraftBlock proposal={proposal} flat /> : null}
      {proposal.kind === 'listing_title' ? <DiffBlock proposal={proposal} /> : null}
      {proposal.kind === 'generic' ? (
        <>
          <GenericFields proposal={proposal} />
          <GenericPreview proposal={proposal} />
          <GenericDiff proposal={proposal} />
        </>
      ) : null}
      <VerifierNotice proposal={proposal} />
      <DesktopDecision
        proposal={proposal}
        blocker={decision.blocker}
        holdRef={holdRef}
        onApprove={(via) => void decision.approve(via)}
        onEdit={decision.edit}
        onReject={decision.reject}
      />
      {run ? <span className="sr-only">Çalıştırma #{run.id}</span> : null}
    </article>
  );
}

function OnaylarPanel({ snapshot, now }: { snapshot: Snapshot; now: number }) {
  const proposals = orderedProposals(snapshot);
  const [first, ...rest] = proposals;
  const restT3 = rest.filter((proposal) => proposal.tier === 'T3');
  const restT4 = rest.filter((proposal) => proposal.tier !== 'T3');
  return (
    <section className="panel panel--scroll" aria-labelledby="h-onaylar">
      <SectionHeader
        id="h-onaylar"
        title="Onaylar"
        count={proposals.length}
        className="panel__head"
        right={<span className="panel__hint">karar verince sıradaki açılır</span>}
        action={{ label: 'Tümü', to: '/onaylar' }}
      />
      {first ? (
        <InlineDecision key={first.id} proposal={first} snapshot={snapshot} now={now} />
      ) : (
        <EmptyState title="Kuyruk boş" text="Ekip çalışıyor. Bir şey gelince Telegram'dan haber veririz." />
      )}
      {restT3.map((proposal) => (
        <ApprovalRow key={proposal.id} proposal={proposal} now={now} desktop />
      ))}
      {restT4.length > 0 && first?.tier !== 'T4' ? <T4Band desktop /> : null}
      {restT4.map((proposal) => (
        <ApprovalRow key={proposal.id} proposal={proposal} now={now} desktop />
      ))}
      <ExpiredLine snapshot={snapshot} />
    </section>
  );
}

function RequestBrief({ request, now }: { request: TwinRequest; now: number }) {
  const sheets = useSheets();
  const titleId = `brief-${request.id}`;
  if (request.type !== 'design') return null;
  return (
    <article className="brief-card" aria-labelledby={titleId}>
      <span className="inline-card__bar" aria-hidden="true" />
      <div className="rcard__head">
        <TypeChip type={request.type} />
        <span className="mono ink-2 brief-card__age">{formatAge(now - request.createdAt)}</span>
      </div>
      <h3 id={titleId} className="brief-card__title">
        {request.title}
      </h3>
      <div className="rcard__from">
        <AgentGlyph shape={AGENT_SHAPES[request.agent]} />
        {AGENT_NAMES[request.agent]} · <span className="mono">{formatRelativeClock(request.createdAt, now)}</span> ·{' '}
        {request.origin}
      </div>
      <dl className="brief-card__table">
        {request.desktopBrief.map((row) => (
          <div key={row.key} className="brief-card__row">
            <dt className="key ink-2">{row.key}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
        <div className="brief-card__row">
          <dt className="key ink-2">Teslim</dt>
          <dd>
            {request.due.value} {request.due.note}
            <span className="brief-card__path mono">{request.deliveryFolder}</span>
          </dd>
        </div>
        <div className="brief-card__row">
          <dt className="key ink-2">Fiyat</dt>
          <dd className="brief-card__price">
            Hedef fiyat ayrıca onayına gelir
            <span className="tier tier--t4 tier--mini">
              <Icon name="lock" size={10} stroke={2.5} />
              T4
            </span>
          </dd>
        </div>
      </dl>
      <div className="brief-card__actions">
        <button
          type="button"
          className="btn btn--primary btn--compact brief-card__accept"
          aria-label="Kabul et ve hedef tarih seç"
          onClick={() => sheets.open({ kind: 'accept', requestId: request.id })}
        >
          <Icon name="calendar" size={16} />
          Kabul · tarih seç
        </button>
        <button
          type="button"
          className="btn btn--secondary btn--compact"
          onClick={() => sheets.open({ kind: 'defer', requestId: request.id })}
        >
          <Icon name="clock" size={16} />
          Ertele
        </button>
        <button
          type="button"
          className="btn btn--secondary btn--compact"
          onClick={() => sheets.open({ kind: 'request-reject', requestId: request.id })}
        >
          <Icon name="x" size={16} />
          Reddet
        </button>
      </div>
    </article>
  );
}

function RequestsPanel({ snapshot, now }: { snapshot: Snapshot; now: number }) {
  const sheets = useSheets();
  const open = openRequests(snapshot);
  const work = open.filter((request) => request.type !== 'question');
  const questions = open.filter((request) => request.type === 'question');
  const [firstWork, ...otherWork] = work;
  const slots = snapshot.requestSlots;
  return (
    <section className="panel panel--scroll" aria-labelledby="h-isler">
      <SectionHeader
        id="h-isler"
        title="Senden beklenenler"
        count={open.length}
        className="panel__head"
        right={
          <span className="panel__aside">
            iş sınırı <span className="mono">{`${slots.used}/${slots.max}`}</span>
            {slots.used >= slots.max ? ' · dolu' : ''}
          </span>
        }
      />
      {firstWork ? <RequestBrief request={firstWork} now={now} /> : null}
      {otherWork.map((request) => (
        <Link key={request.id} to={`/istek/${request.id}`} className="work-row">
          <span className="work-row__body">
            <span className="work-row__head">
              <TypeChip type={request.type} />
              <span className="ink-2 work-row__due">
                {request.type === 'photo' ? (
                  <>
                    son tarih <span className="mono">{request.dueLabel}</span>
                  </>
                ) : (
                  <span className="mono">{formatAge(now - request.createdAt)}</span>
                )}
              </span>
            </span>
            <span className="work-row__title">{request.title}</span>
            <span className="work-row__meta">
              {request.type === 'photo' ? request.desktopSummary : request.context}
            </span>
          </span>
          <Icon name="chevron-right" size={18} className="ink-3" />
        </Link>
      ))}
      {questions.length > 0 ? (
        <>
          <div className="unknown-band">
            <h3 className="key ink-2">Bilinmeyen sorular · {questions.length}</h3>
            <span className="ink-2">cevap → bilgi bankası</span>
          </div>
          {questions.map((request, index) => (
            <div key={request.id} className="question-row">
              <TypeChip type="question" variant="plain" />
              <div className="question-row__body">
                <p className="question-row__title">{request.title}</p>
                <div className="question-row__meta">
                  {request.type === 'question' ? request.country : request.origin} · müşteri bekliyor{' '}
                  <span className="mono">{formatAge(now - request.createdAt)}</span>
                </div>
              </div>
              <button
                type="button"
                className={index === 0 ? 'btn btn--primary btn--small' : 'btn btn--secondary btn--small'}
                aria-label={`Soruyu cevapla: ${request.title}. Cevabın bilgi bankasına girer`}
                onClick={() => sheets.open({ kind: 'answer', requestId: request.id })}
              >
                Cevapla
              </button>
            </div>
          ))}
        </>
      ) : null}
    </section>
  );
}

function Rail({ snapshot, now }: { snapshot: Snapshot; now: number }) {
  const team = agentCounts(snapshot.agents);
  return (
    <div className="rail">
      <ReadinessPanel items={snapshot.readiness} now={now} />
      <section className="panel" aria-labelledby="h-gm">
        <SectionHeader
          id="h-gm"
          title="GM sabah notu"
          className="panel__head"
          right={
            <span className="panel__aside panel__aside--glyph">
              <AgentGlyph shape="circle" size={12} />
              {snapshot.gmNote.model} · <span className="mono">{formatClock(snapshot.gmNote.at)}</span>
            </span>
          }
        />
        <div className="panel__body">
          <p className="rail-note">
            <strong>{snapshot.gmNote.lead}</strong> {snapshot.gmNote.body}
          </p>
          <p className="rail-note__source">
            {snapshot.gmNote.source.replace(/ · \d+ \S+$/, '')} · <span className="mono">26 Eyl</span>
          </p>
        </div>
      </section>
      <section className="panel" aria-labelledby="h-ekip">
        <SectionHeader
          id="h-ekip"
          title="Ekip şimdi"
          className="panel__head"
          right={
            <span className="panel__hint panel__hint--ink2" aria-live="polite">
              {team.working} çalışıyor · {team.error === 0 ? 'hata yok' : `${team.error} hata`}
            </span>
          }
          action={{ label: 'Ekip', to: '/ekip' }}
        />
        {snapshot.agents.map((agent) => (
          <div key={agent.id} className="rail-agent">
            <AgentGlyph shape={agent.shape} />
            <span className="rail-agent__head">
              <span className="rail-agent__name">{agent.name}</span>
              <StatusPill state={agent.state} />
            </span>
            <span className="mono rail-agent__money">
              {agent.budget.kind === 'genesis'
                ? `${formatUsdNumber(agent.budget.spent)} / ${formatUsd(agent.budget.cap, 'auto')}`
                : `5 sa ${formatQuotaPct(agent.budget.window5hPct)}`}
            </span>
            <span className="rail-agent__line ellipsis">
              {agent.progress && agent.state === 'working' ? (
                <>
                  <span className="mono">{agent.progress.doneSteps + 1}.</span> adım · {agent.progress.currentStep}
                </>
              ) : agent.next ? (
                <>
                  sonraki <span className="mono">{formatClock(agent.next.at)}</span> {agent.next.label}
                </>
              ) : (
                agent.task
              )}
            </span>
            {agent.budget.kind === 'claude_max' ? (
              <span className="rail-agent__week">
                hafta <span className="mono">{formatQuotaPct(agent.budget.weekPct)}</span>
              </span>
            ) : null}
          </div>
        ))}
      </section>
      <section className="panel panel--fill" aria-labelledby="h-satis">
        <SectionHeader
          id="h-satis"
          title={`${snapshot.store.monthLabel} satışları`}
          count={snapshot.store.orders}
          className="panel__head"
          right={
            <span className="panel__aside">
              tüm zamanlar <span className="mono">{formatUsd(snapshot.store.allTime.revenue, 0)}</span>
            </span>
          }
        />
        <div className="panel__rows">
          {snapshot.store.sales.map((sale) => (
            <SalesRow key={sale.id} sale={sale} />
          ))}
        </div>
      </section>
    </div>
  );
}

export function BugunDesktop() {
  const state = usePanel();
  const snapshot = state.snapshot;
  const team = snapshot ? agentCounts(snapshot.agents) : null;
  const status = <TopBarStatus extra={`${team?.working ?? 0} çalışıyor`} />;
  return (
    <>
      <TopBar title="Bugün" status={status} />
      {snapshot ? (
        <main className="desk-main">
          <div className="bugun-head">
            <InboxCard snapshot={snapshot} now={state.now} />
            <PulseTiles snapshot={snapshot} desktop />
          </div>
          <div className="bugun-grid">
            <OnaylarPanel snapshot={snapshot} now={state.now} />
            <RequestsPanel snapshot={snapshot} now={state.now} />
            <Rail snapshot={snapshot} now={state.now} />
          </div>
          <TodaySection snapshot={snapshot} desktop />
        </main>
      ) : (
        <Loading />
      )}
    </>
  );
}

type QueueFilter = 'tumu' | 'onay' | 'istek';

/** Desktop Onaylar: queue on the left, the selected item's evidence and decision on the right. */
export function OnaylarDesktop({ id }: { id: string | null }) {
  const state = usePanel();
  const snapshot = state.snapshot;
  const holdRef = useRef<HoldHandle>(null);
  const [filter, setFilter] = useState<QueueFilter>('tumu');
  const proposals = snapshot ? orderedProposals(snapshot) : [];
  const requests = snapshot ? openRequests(snapshot) : [];
  // An id in the URL selects exactly that item. When it has left the queue (approved, applied,
  // rejected elsewhere) the pane says so instead of silently showing another item: no
  // auto-advance after a T4, and no surprise switch under a hand that is about to decide.
  const selected = id === null ? (proposals[0] ?? null) : (proposals.find((proposal) => proposal.id === id) ?? null);
  const index = selected ? proposals.indexOf(selected) : -1;
  const decision = useDecision(
    selected,
    (nextId) => {
      if (nextId) navigate(`/onay/${nextId}`, { replace: true });
    },
    { onHeldApproved: (proposalId) => navigate(`/onay/${proposalId}`, { replace: true }) },
  );
  const move = (delta: 1 | -1) => {
    const target = proposals[index + delta];
    if (target) navigate(`/onay/${target.id}`, { replace: true });
  };
  useDecisionKeys({
    enabled: selected !== null,
    proposal: selected,
    onApprove: (via) => void decision.approve(via),
    onEdit: decision.edit,
    onReject: decision.reject,
    onMove: move,
    holdRef,
  });
  const c = snapshot ? counts(snapshot) : null;
  const pendingT4 = id
    ? state.pending.find((item) => item.proposal.id === id && requiresHold(item.proposal))
    : undefined;
  return (
    <>
      <TopBar
        title="Onaylar"
        status={<TopBarStatus extra={`${c?.approvals ?? 0} onay · ${c?.requests ?? 0} istek bekliyor`} />}
      />
      {snapshot && c ? (
        <main className="desk-main desk-main--panes">
          <section className="panel queue-pane" aria-labelledby="h-kuyruk">
            <SectionHeader id="h-kuyruk" title="Senden bekleyen" count={c.total} className="panel__head" />
            <div className="queue-pane__seg">
              <Segmented<QueueFilter>
                label="Kuyruk süzgeci"
                controls="kuyruk"
                value={filter}
                onChange={setFilter}
                options={[
                  { id: 'tumu', label: 'Tümü', count: c.total },
                  { id: 'onay', label: 'Onay', count: c.approvals },
                  { id: 'istek', label: 'İstek', count: c.requests },
                ]}
              />
            </div>
            <div className="queue-pane__list" id="kuyruk" role="tabpanel" aria-labelledby={`tab-kuyruk-${filter}`}>
              {filter !== 'istek' ? (
                <>
                  <div className="queue-pane__group key ink-2">Onay bekliyor · {c.approvals}</div>
                  {proposalGroups(proposals).map((group, index, groups) => (
                    <div key={`${group.tier}-${group.label}`} className="queue-group-block">
                      {group.tier !== 'T3' && (index === 0 || groups[index - 1]?.tier === 'T3') ? (
                        <T4Band desktop />
                      ) : null}
                      <QueueGroupHead label={group.label} count={group.items.length} />
                      {group.items.map((proposal) => (
                        <div
                          key={proposal.id}
                          className={
                            proposal.id === selected?.id
                              ? 'queue-pane__item queue-pane__item--selected'
                              : 'queue-pane__item'
                          }
                        >
                          <ApprovalRow proposal={proposal} now={state.now} desktop />
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              ) : null}
              {filter !== 'onay' ? (
                <>
                  <div className="queue-pane__group key ink-2">Senden istenen · {c.requests}</div>
                  {requests.map((request) => (
                    <RequestRow key={request.id} request={request} now={state.now} />
                  ))}
                </>
              ) : null}
            </div>
            <div className="queue-pane__keys">
              <Kbd>J</Kbd> <Kbd>K</Kbd> gezin · <Kbd>O</Kbd> onayla · <Kbd>D</Kbd> düzelt · <Kbd>R</Kbd> reddet
            </div>
          </section>
          <section className="panel detail-pane" aria-label="Onay ayrıntısı">
            {selected ? (
              <>
                <div className="detail-pane__head">
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label="Önceki onay"
                    disabled={index <= 0}
                    onClick={() => move(-1)}
                  >
                    <Icon name="chevron-up" size={16} />
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label="Sonraki onay"
                    disabled={index >= proposals.length - 1}
                    onClick={() => move(1)}
                  >
                    <Icon name="chevron-down" size={16} />
                  </button>
                  <span className="detail-pane__count">
                    Onay{' '}
                    <span className="mono">
                      {index + 1} / {proposals.length}
                    </span>
                  </span>
                  <span className="detail-pane__age">
                    Bekliyor · <span className="mono">{formatAge(state.now - selected.queuedAt)}</span>
                  </span>
                </div>
                <div className="detail-pane__body">
                  <ProposalBody proposal={selected} snapshot={snapshot} desktop titleId={`pane-${selected.id}`} />
                </div>
                <div className="detail-pane__foot">
                  <p className="detail-pane__consequence">{selected.executor.consequence}</p>
                  <DesktopDecision
                    proposal={selected}
                    blocker={decision.blocker}
                    holdRef={holdRef}
                    onApprove={(via) => void decision.approve(via)}
                    onEdit={decision.edit}
                    onReject={decision.reject}
                  />
                </div>
              </>
            ) : pendingT4 ? (
              <div className="all-done">
                <Icon name="check" size={24} stroke={2} className="ok" />
                <p className="all-done__title">Onaylandı · {pendingT4.proposal.title}</p>
                <p className="all-done__text">Yürütücü 10 sn bekler; bu sürede bildirimden geri alabilirsin.</p>
                <button type="button" className="btn btn--primary" onClick={() => navigate('/onaylar')}>
                  Kuyruğa dön
                </button>
              </div>
            ) : proposals.length > 0 ? (
              <div className="all-done">
                <p className="all-done__title">Bu onay artık kuyrukta değil</p>
                <button type="button" className="btn btn--primary" onClick={() => navigate('/onaylar')}>
                  Kuyruğa dön
                </button>
              </div>
            ) : (
              <div className="all-done">
                <Icon name="check" size={24} stroke={2} className="ok" />
                <p className="all-done__title">Hepsi bitti · her şey yolunda</p>
                <button type="button" className="btn btn--primary" onClick={() => navigate('/')}>
                  Bugün'e dön
                </button>
              </div>
            )}
          </section>
        </main>
      ) : (
        <Loading />
      )}
    </>
  );
}

export function IsteklerDesktop({ focus }: { focus: string | null }) {
  const state = usePanel();
  const snapshot = state.snapshot;
  const { tab, setTab, lists, options } = useRequestTabs(snapshot);
  useFocusRequest(focus, snapshot !== null);
  const c = snapshot ? counts(snapshot) : null;
  return (
    <>
      <TopBar title="Senden beklenenler" status={<TopBarStatus extra={snapshot ? requestSubline(snapshot) : ''} />} />
      {snapshot && c ? (
        <main className="desk-main">
          <div className="desk-toolbar">
            <p className="desk-toolbar__title">
              <Accent>{c.requests}</Accent> istek senden bekleniyor
            </p>
            <Segmented label="İstek durumu" controls="ist-panel" value={tab} onChange={setTab} options={options} />
          </div>
          <div id="ist-panel" role="tabpanel" aria-labelledby={`tab-ist-panel-${tab}`} className="rcards rcards--grid">
            {lists[tab].length === 0 ? (
              <EmptyState
                title="Ekip senden bir şey beklemiyor"
                text="Bir soru ya da tasarım isteği gelince burada görünür."
              />
            ) : (
              lists[tab].map((request) => (
                <RequestCard key={request.id} request={request} now={state.now} focused={focus === request.id} />
              ))
            )}
          </div>
        </main>
      ) : (
        <Loading />
      )}
    </>
  );
}

function EkipKpis({ snapshot, now }: { snapshot: Snapshot; now: number }) {
  const tick = monthProgress(now);
  const cap = snapshot.genesis.cap;
  const ratio = snapshot.genesis.spent / cap;
  const pace = budgetPace(ratio, tick);
  const genesisAgents = snapshot.agents.filter((agent) => agent.budget.kind === 'genesis');
  const gm = snapshot.agents.find((agent) => agent.budget.kind === 'claude_max');
  let offset = 0;
  const opacities = [1, 0.65, 0.4];
  return (
    <section className="kpis kpis--ekip drafting" aria-label="Bütçe, yürütücü ve program">
      <div className="kpi">
        <div className="kpi__head kpi__head--split">
          <span className="key ink-2">
            <span lang="en">Genesis</span> · {snapshot.store.monthLabel}
          </span>
          <span className="kpi__small mono">tavan {formatUsd(cap, 'auto')}</span>
        </div>
        <div className="kpi__genesis">
          <span className="kpi__num mono">{formatUsd(snapshot.genesis.spent)}</span>
          <div className="kpi__stack">
            {/* biome-ignore lint/a11y/useSemanticElements: <meter> cannot draw per-agent segments and the month tick. */}
            <div
              className="meter meter--h6"
              role="meter"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(ratio * 100)}
              aria-label={`Genesis bu ayki harcaması ${formatUsd(snapshot.genesis.spent)}, ${formatUsd(cap, 'auto')} tavanın yüzde ${Math.round(ratio * 100)}'si. ${genesisAgents
                .map((agent) => `${agent.name} ${agent.budget.kind === 'genesis' ? formatUsd(agent.budget.spent) : ''}`)
                .join(', ')}. Ayın yüzde ${Math.round(tick * 100)}'ı geçti.`}
            >
              {genesisAgents.map((agent, index) => {
                const spent = agent.budget.kind === 'genesis' ? agent.budget.spent : 0;
                const left = offset;
                offset += spent / cap;
                return (
                  <span
                    key={agent.id}
                    className={`meter__seg meter__fill--${pace}`}
                    style={{
                      left: `${left * 100}%`,
                      width: `${(spent / cap) * 100}%`,
                      opacity: opacities[index] ?? 0.4,
                    }}
                  />
                );
              })}
              <span className="meter__tick" style={{ left: `${tick * 100}%` }} />
            </div>
            <div className="kpi__legend mono" aria-hidden="true">
              {genesisAgents.map((agent, index) => (
                <span key={agent.id} style={{ opacity: index === 0 ? 1 : undefined }}>
                  <AgentGlyph shape={agent.shape} size={10} style={{ opacity: opacities[index] ?? 0.4 }} />
                  {agent.budget.kind === 'genesis' ? formatUsdNumber(agent.budget.spent) : ''}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="kpi__line">
          ay {formatPct(tick)} · harcama {formatPct(ratio)} ·{' '}
          <span className={pace === 'ok' ? undefined : 'warning'}>{pace === 'ok' ? 'tempoda' : 'tempo önde'}</span>
        </div>
      </div>
      {gm && gm.budget.kind === 'claude_max' ? (
        <div className="kpi">
          <div className="kpi__head kpi__head--split">
            <span className="key ink-2">GM aboneliği</span>
            <span className="kpi__small mono">hafta {formatQuotaPct(gm.budget.weekPct)}</span>
          </div>
          <div className="kpi__num-row kpi__num-row--base">
            <span className="kpi__num mono">{formatQuotaPct(gm.budget.window5hPct)}</span>
            <span className="key ink-2">5 sa pencere</span>
          </div>
          <div className="kpi__line ellipsis">
            API karşılığı <span className="mono">{formatUsd(gm.budget.apiEquivalent)}</span> · faturalanmaz
          </div>
        </div>
      ) : null}
      <div className="kpi">
        <div className="kpi__head kpi__head--split">
          <span className="key ink-2">Yürütücü</span>
          <span className="kpi__small">
            geri alma <span className="mono">{snapshot.executor.undoSeconds} sn</span>
          </span>
        </div>
        <div className="kpi__words kpi__words--icon">
          {snapshot.executor.ready ? (
            <Icon name="check" size={18} stroke={2.5} className="ok" />
          ) : (
            <span className="kill__square" aria-hidden="true" />
          )}
          {snapshot.executor.ready ? 'Hazır' : 'Durdu'}
        </div>
        <div className="kpi__line">
          bekleyen <span className="mono">{snapshot.executor.pending}</span>
          {snapshot.executor.lastAppliedAt ? (
            <>
              {' · son uygulama '}
              {formatRelativeClock(snapshot.executor.lastAppliedAt, now).replace(/^(\S+) /, '$1 ')}
            </>
          ) : null}
        </div>
      </div>
      <div className="kpi kpi--ruler">
        <span className="key ink-2 kpi__ruler-key">Program</span>
        <ProgramRuler schedule={snapshot.schedule} now={now} compact />
      </div>
    </section>
  );
}

export function EkipDesktop({ run: runId }: { run: number | null }) {
  const state = usePanel();
  const snapshot = state.snapshot;
  const team = snapshot ? agentCounts(snapshot.agents) : null;
  // Deep link /ekip/:run selects that run; otherwise the latest handoff with a finished run log.
  const finished = (id: number | null) => snapshot?.runs.some((run) => run.id === id && run.status === 'done') ?? false;
  const defaultHandoff =
    snapshot?.handoffs.find((handoff) => runId !== null && handoff.runId === runId) ??
    snapshot?.handoffs.find((handoff) => finished(handoff.runId)) ??
    snapshot?.handoffs[0];
  const [selected, setSelected] = useState<string | null>(null);
  const selectedHandoff = snapshot?.handoffs.find((handoff) => handoff.id === (selected ?? defaultHandoff?.id));
  const run = snapshot?.runs.find((item) => item.id === selectedHandoff?.runId) ?? null;
  const total = run?.finishedAt ? Math.round((run.finishedAt - run.startedAt) / 1000) : null;
  return (
    <>
      <TopBar
        title="Ekip"
        status={<TopBarStatus to="/kayitlar" extra={team ? `${team.working} çalışıyor · ${teamSubline(team)}` : ''} />}
        actions={<PauseAllButton desktop />}
      />
      {snapshot ? (
        <main className="desk-main">
          <EkipKpis snapshot={snapshot} now={state.now} />
          <section className="tiles" aria-label="Ajanlar">
            {snapshot.agents.map((agent) => (
              <AgentTile key={agent.id} agent={agent} now={state.now} />
            ))}
          </section>
          <div className="ekip-grid">
            <div className="ekip-grid__left">
              <section className="panel" aria-labelledby="ek-devir">
                <SectionHeader
                  id="ek-devir"
                  title="Devir akışı"
                  count={snapshot.handoffs.length}
                  className="panel__head"
                  right={
                    <>
                      <span className="panel__hint panel__hint--ink2">bugün</span>
                      <span className="lanes-legend" aria-hidden="true">
                        <span>
                          <span className="lanes-legend__solid" />
                          devretti
                        </span>
                        <span>
                          <span className="lanes-legend__dashed" />
                          bekliyor
                        </span>
                        <span>
                          <span className="node-live" />
                          sürüyor
                        </span>
                        <span>
                          <Icon name="check" size={12} stroke={2.5} className="ok" />
                          geçti
                        </span>
                      </span>
                    </>
                  }
                />
                <HandoffLanes
                  handoffs={snapshot.handoffs}
                  selected={selectedHandoff?.id ?? null}
                  onSelect={setSelected}
                />
              </section>
              <section className="panel panel--fill" aria-labelledby="ek-canli">
                <div className="section-header panel__head">
                  <LiveDot />
                  <h2 id="ek-canli" className="section-header__title">
                    Canlı akış
                  </h2>
                  <span className="panel__hint panel__hint--ink2">
                    Ajanlar iş gelince çalışır; aradaki sessizlik normal.
                  </span>
                  <Link to="/kayitlar" className="section-header__action">
                    Kayıtlar
                    <Icon name="chevron-right" size={16} />
                  </Link>
                </div>
                <LiveFeed items={snapshot.feed} />
              </section>
            </div>
            <section className="panel run-panel" aria-labelledby="ek-run">
              {run && selectedHandoff ? (
                <>
                  <div className="section-header panel__head run-panel__head">
                    <AgentGlyph shape={AGENT_SHAPES[run.agent]} size={16} />
                    <h2 id="ek-run" className="section-header__title nowrap">
                      {AGENT_NAMES[run.agent]} · Çalıştırma #{run.id}
                    </h2>
                    <span className="done-chip">
                      {run.status === 'done' ? (
                        <>
                          <Icon name="check" size={12} stroke={2.5} className="ok" />
                          Bitti <span className="mono">{run.finishedAt ? formatClock(run.finishedAt) : ''}</span>
                        </>
                      ) : (
                        <>
                          <span className="node-live" />
                          Sürüyor
                        </>
                      )}
                    </span>
                    <Link to="/kayitlar" className="ghost-link">
                      <Icon name="code" size={16} />
                      Ham kayıt
                    </Link>
                  </div>
                  <div className="run-panel__body">
                    <div className="run-panel__title-row">
                      <h3 className="run-panel__title">{run.title}</h3>
                      <Link to="/onaylar" className="run-panel__open">
                        Onay kuyruğunda aç
                        <Icon name="chevron-right" size={16} />
                      </Link>
                    </div>
                    <p className="run-panel__subtitle ellipsis">{run.subtitle}</p>
                    <ol className="chain" aria-label="Kaynak zinciri">
                      {run.provenance.map((item, index) => (
                        <li key={item} className="chain__item">
                          {index > 0 ? <Icon name="arrow-right" size={14} className="ink-3 chain__arrow" /> : null}
                          <span
                            className={
                              index === run.provenance.length - 1 && item.startsWith('Onay')
                                ? 'chain__chip chain__chip--end'
                                : 'chain__chip'
                            }
                          >
                            {item === 'Telegram' ? <Icon name="send" size={12} /> : null}
                            {item === 'Temsilci' ? <AgentGlyph shape="square" size={12} /> : null}
                            {item === 'Denetçi' ? <AgentGlyph shape="hexagon" size={12} /> : null}
                            {item}
                          </span>
                        </li>
                      ))}
                    </ol>
                    <RunLog run={run} now={state.now} />
                    {run.authorSeconds !== null && run.verifierSeconds !== null && total ? (
                      <div
                        className="dimension"
                        role="img"
                        aria-label={`Süre: ${AGENT_NAMES[run.agent]} ${run.authorSeconds} saniye, Denetçi ${run.verifierSeconds} saniye ayrı çalıştırmada. ${formatClockSeconds(run.startedAt)} ile ${run.finishedAt ? formatClockSeconds(run.finishedAt) : ''} arası.`}
                      >
                        <span
                          className="dimension__label"
                          style={{ left: 0, width: `${(run.authorSeconds / total) * 100}%` }}
                        >
                          <AgentGlyph shape={AGENT_SHAPES[run.agent]} size={10} />
                          {AGENT_NAMES[run.agent]} <span className="mono ink">{formatDuration(run.authorSeconds)}</span>
                        </span>
                        <span
                          className="dimension__label"
                          style={{
                            left: `${(run.authorSeconds / total) * 100}%`,
                            width: `${(run.verifierSeconds / total) * 100}%`,
                          }}
                        >
                          <AgentGlyph shape="hexagon" size={10} />
                          Denetçi{' '}
                          <span className="mono ink dimension__strong">{formatDuration(run.verifierSeconds)}</span> ·
                          ayrı çalıştırma
                        </span>
                        <span className="dimension__line" />
                        <span className="dimension__tick" style={{ left: 0 }} />
                        <span className="dimension__tick" style={{ left: `${(run.authorSeconds / total) * 100}%` }} />
                        <span
                          className="dimension__tick"
                          style={{ left: `${((run.authorSeconds + run.verifierSeconds) / total) * 100}%` }}
                        />
                        <span className="dimension__tick" style={{ left: 'calc(100% - 1px)' }} />
                        <span className="dimension__time mono" style={{ left: 0 }}>
                          {formatClockSeconds(run.startedAt)}
                        </span>
                        <span
                          className="dimension__time dimension__time--mid mono"
                          style={{ left: `${(run.authorSeconds / total) * 100}%` }}
                        >
                          {formatClockSeconds(run.startedAt + run.authorSeconds * 1000)}
                        </span>
                        <span className="dimension__time mono" style={{ right: 0 }}>
                          {run.finishedAt ? formatClockSeconds(run.finishedAt) : ''}
                        </span>
                      </div>
                    ) : null}
                    <dl className="facts facts--4 run-facts">
                      <div className="facts__cell">
                        <dt className="key ink-2">Toplam</dt>
                        <dd className="mono run-facts__big">
                          {formatDuration(Math.round(((run.finishedAt ?? state.now) - run.startedAt) / 1000))}
                        </dd>
                      </div>
                      <div className="facts__cell">
                        <dt className="key ink-2">Maliyet</dt>
                        <dd className="mono run-facts__big">{formatUsd(run.cost)}</dd>
                      </div>
                      <div className="facts__cell">
                        <dt className="key ink-2">Yazan</dt>
                        <dd className="mono run-facts__small ellipsis">{run.authorModel}</dd>
                      </div>
                      <div className="facts__cell">
                        <dt className="key ink-2">Denetleyen</dt>
                        <dd className="mono run-facts__small ellipsis">{run.verifierModel ?? 'henüz yok'}</dd>
                      </div>
                    </dl>
                  </div>
                </>
              ) : (
                <div className="all-done">
                  <p className="all-done__title" id="ek-run">
                    Bu devrin çalıştırma kaydı Kayıtlar'da
                  </p>
                  <Link to="/kayitlar" className="btn btn--secondary">
                    Kayıtlar
                  </Link>
                </div>
              )}
            </section>
          </div>
          <OrganizationToggle />
        </main>
      ) : (
        <Loading />
      )}
    </>
  );
}
